import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { User } from '@/lib/types';

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: () => boolean;
  isAdmin: () => boolean;
  setSession: (user: User, accessToken: string) => void;
  setAccessToken: (token: string) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      // Présence du user = optimisme d'auth (ADR 0002 D1). L'accessToken vit
      // en mémoire uniquement ; un 401 réel passe par l'interceptor refresh
      // (clear + redirect déjà en place dans api.ts).
      isAuthenticated: () => Boolean(get().user),
      isAdmin: () => Boolean(get().user?.isAdmin),
      setSession: (user, accessToken) => set({ user, accessToken }),
      setAccessToken: (accessToken) => set({ accessToken }),
      clear: () => set({ user: null, accessToken: null }),
    }),
    {
      name: 'myfood:auth',
      // JAMAIS l'accessToken en localStorage (ADR 0002 D1 — N1 fermé) : seul
      // le user est persisté. Le refresh token est un cookie HttpOnly ; le
      // bootstrap au montage de App.tsx redemande un accessToken si besoin.
      partialize: (s) => ({ user: s.user }),
      // Merge explicite : le merge par défaut de zustand fait un spread du
      // blob désérialisé tel quel (`{...currentState, ...persistedState}`),
      // donc un ANCIEN blob localStorage contenant encore `accessToken`
      // (pré-migration) le réinjecterait en mémoire au rehydrate — vérifié
      // empiriquement, contrairement à l'hypothèse « clés en trop ignorées ».
      // On ne reprend donc explicitement que `user` du blob persisté.
      merge: (persistedState, currentState) => ({
        ...currentState,
        user: (persistedState as Partial<AuthState> | undefined)?.user ?? currentState.user,
      }),
    },
  ),
);
